import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import {
  API_BASE_URL,
  setCsrfToken as setApiCsrfToken,
  clearCsrfToken as clearApiCsrfToken,
  registerCsrfRefresher,
} from './lib/api';
import { isValidOauthReturnTo } from './lib/oauthReturnTo';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [csrfToken, setCsrfToken] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Fetch CSRF token when user is authenticated
  useEffect(() => {
    if (user && !csrfToken) {
      fetchCsrfToken();
    }
  }, [user, csrfToken]);

  // Keep the shared api client's cached CSRF token in sync with React state
  // so the request interceptor in lib/api.js can attach it.
  useEffect(() => {
    setApiCsrfToken(csrfToken);
  }, [csrfToken]);

  const fetchCsrfToken = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/csrf-token`);
      setCsrfToken(response.data.csrfToken);
      return response.data.csrfToken;
    } catch (err) {
      console.error('Failed to fetch CSRF token:', err);
      return null;
    }
  };

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`);
      setUser(response.data);
      setError(null);
    } catch (err) {
      setUser(null);
      setCsrfToken(null); // Clear CSRF token when user logs out
      clearApiCsrfToken();
      // Don't set error for 401 - just means not logged in
      if (err.response?.status !== 401) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (returnTo) => {
    // Redirect to backend Google OAuth. `returnTo` (issue #257) lets the
    // caller — the OAuth consent page's logged-out path, via Login — land
    // back on /oauth/consent?request=<id> after signing in instead of the
    // user's normal landing page. Re-validated here (same regex as the
    // backend) so an unexpected value is silently dropped rather than
    // forwarded; the backend validates it again independently regardless.
    const url = isValidOauthReturnTo(returnTo)
      ? `${API_BASE_URL}/auth/google?returnTo=${encodeURIComponent(returnTo)}`
      : `${API_BASE_URL}/auth/google`;
    window.location.href = url;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/auth/logout`);
      setUser(null);
      setCsrfToken(null); // Clear CSRF token on logout
      clearApiCsrfToken();
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  // Allow the shared api client to refresh the CSRF token when it sees an
  // EBADCSRFTOKEN response. The interceptor itself lives in lib/api.js.
  useEffect(() => {
    const unregister = registerCsrfRefresher(async () => {
      const newToken = await fetchCsrfToken();
      return newToken;
    });
    return unregister;
  }, []);

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
    csrfToken,
    fetchCsrfToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
